import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import "./RateTableComponents.css";

// ─────────────────────────────────────────────
// Formatting Utilities
// ─────────────────────────────────────────────
export const fmtRupee = (v) =>
  v != null && !isNaN(v)
    ? `₹${Number(v).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      })}`
    : "—";

export const fmtPct = (v) => {
  const n = Number(v);
  if (isNaN(n)) return "0.00%";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

export const getSign = (v) => {
  const n = Number(v);
  if (n > 0) return "positive";
  if (n < 0) return "negative";
  return "zero";
};

// ─────────────────────────────────────────────
// Base Rate Table Columns
// ─────────────────────────────────────────────

/**
 * Metal Name Column - Left-aligned with color indicator
 */
export const MetalNameCell = ({ metal, metalMeta }) => (
  <div className="rc-metal-name-cell">
    <span
      className="rc-metal-dot"
      style={{ background: metalMeta.color }}
    />
    <strong style={{ color: metalMeta.color }}>{metal.label}</strong>
  </div>
);

/**
 * Spot Price Cell - USD per ounce
 */
export const SpotPriceCell = ({ spotUsd, loading }) => (
  <div className="rc-spot-price-cell">
    {loading || spotUsd == null ? (
      <span className="rc-skeleton rc-skeleton--sm" />
    ) : (
      <strong>${spotUsd}</strong>
    )}
  </div>
);

/**
 * USD to INR Exchange Rate Cell
 */
export const ExchangeRateCell = ({ usdToInr, loading }) => (
  <div className="rc-exchange-rate-cell">
    {loading || usdToInr == null ? (
      <span className="rc-skeleton rc-skeleton--sm" />
    ) : (
      <strong>₹{usdToInr}</strong>
    )}
  </div>
);

/**
 * Premium Input Cell - Editable percentage
 */
export const PremiumInputCell = ({
  metalKey,
  premiumValue,
  loading,
  onChange,
  onBlur,
}) => (
  <div className="rc-premium-input-cell">
    {loading ? (
      <span className="rc-skeleton rc-skeleton--sm" />
    ) : (
      <div className="rc-premium-input-wrap">
        <input
          type="number"
          step="0.001"
          value={premiumValue}
          onChange={(e) => onChange(metalKey, e.target.value)}
          onBlur={onBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          className="rc-premium-input"
          aria-label={`Premium percentage`}
        />
        <span>%</span>
      </div>
    )}
  </div>
);

/**
 * Calculated Rate Cell (1g or 10g)
 */
export const CalculatedRateCell = ({ rate, label, loading, isPrimary = false }) => (
  <div className={`rc-calculated-rate-cell ${isPrimary ? "rc-calculated-rate-cell--primary" : ""}`}>
    {loading || rate == null ? (
      <span className="rc-skeleton rc-skeleton--sm" />
    ) : (
      <strong>{fmtRupee(rate)}</strong>
    )}
  </div>
);

/**
 * State Adjustment Amount Cell - Shows the base rupee value of adjustment
 */
export const StateAdjustmentAmountCell = ({ baseRate, adjustmentPct, loading }) => {
  const adjustmentAmount =
    baseRate && !isNaN(baseRate)
      ? baseRate * (Number(adjustmentPct) / 100)
      : 0;

  return (
    <div className="rc-adjustment-amount-cell">
      {loading ? (
        <span className="rc-skeleton rc-skeleton--sm" />
      ) : (
        <>
          <strong>{fmtRupee(adjustmentAmount)}</strong>
          <small>Adj Amount (₹)</small>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// State Rate Table Columns
// ─────────────────────────────────────────────

/**
 * State Rate Chip - Asset Price / Ticker Value (Current state rate)
 */
export const StateRateChip = ({ base, previousBase, adj }) => {
  const stateAdjustmentAmount = base * (Number(adj) / 100);
  const currentStateRate = base + stateAdjustmentAmount;

  const previousStateRate = previousBase
    ? previousBase + (previousBase * (Number(adj) / 100))
    : currentStateRate;

  const netChange = currentStateRate - previousStateRate;
  const sign = getSign(netChange);

  return (
    <div className={`rc-chip rc-chip--state-rate rc-chip--${sign}`}>
      <span className="rc-chip-value">{fmtRupee(currentStateRate)}</span>
      <span className={`rc-chip-badge rc-chip-badge--${sign}`}>
        {sign === "positive" ? "▲" : sign === "negative" ? "▼" : "●"}
      </span>
    </div>
  );
};

/**
 * Net Price Delta Chip - Net Change in rupees
 */
export const NetPriceDeltaChip = ({ base, previousBase, adj }) => {
  const stateAdjustmentAmount = base * (Number(adj) / 100);
  const currentStateRate = base + stateAdjustmentAmount;

  const previousStateRate = previousBase
    ? previousBase + (previousBase * (Number(adj) / 100))
    : currentStateRate;

  const netChange = currentStateRate - previousStateRate;
  const sign = getSign(netChange);

  return (
    <div className={`rc-chip rc-chip--net-delta rc-chip--${sign}`}>
      <span className="rc-chip-amount">
        {sign === "positive" ? "+" : ""}
        {fmtRupee(netChange)}
      </span>
    </div>
  );
};

/**
 * Change Percentage Chip - Percentage change
 */
export const ChangePercentageChip = ({ base, previousBase, adj }) => {
  const stateAdjustmentAmount = base * (Number(adj) / 100);
  const currentStateRate = base + stateAdjustmentAmount;

  const previousStateRate = previousBase
    ? previousBase + (previousBase * (Number(adj) / 100))
    : currentStateRate;

  const netChange = currentStateRate - previousStateRate;
  const changePercent =
    previousStateRate !== 0 ? (netChange / previousStateRate) * 100 : 0;
  const sign = getSign(netChange);

  return (
    <div className={`rc-chip rc-chip--change-pct rc-chip--${sign}`}>
      <span className="rc-chip-pct">
        {sign === "positive" ? "+" : ""}
        {changePercent.toFixed(3)}%
      </span>
    </div>
  );
};

/**
 * State Adjustment Percentage Chip - Displays adjustment %
 */
export const StateAdjustmentPctChip = ({ adj }) => {
  const sign = getSign(adj);
  return (
    <div className={`rc-chip rc-chip--adj-pct rc-chip--${sign}`}>
      <span className="rc-chip-pct">{fmtPct(adj)}</span>
    </div>
  );
};

/**
 * State Rate Chips v2 - Compact, rich UI/UX with responsive design
 * Displays: State Rate (primary), Net Change, Change %, Adjustment Amount
 * Features: Color-coded states, icon indicators, tooltips, hover effects
 */
export const StateRateChips = ({ base, previousBase, adj, activeMetal }) => {
  // Indian Base Rate = base
  // Current Indian Base Rate = base
  const currentIndianBaseRate = base;
  
  // Previous Indian Base Rate = previousBase
  const previousIndianBaseRate = previousBase || base;
  
  // State Rate = Indian Base Rate + (Indian Base Rate × State Constraint%)
  const currentStateRate = currentIndianBaseRate + (currentIndianBaseRate * (Number(adj) / 100));
  const previousStateRate = previousIndianBaseRate + (previousIndianBaseRate * (Number(adj) / 100));
  
  // Net Change in State Rate = Current State Rate - Previous State Rate
  const netChange = currentStateRate - previousStateRate;
  
  // Net Change % based on Indian Base Rate change
  // Formula: ((Current Indian Base Rate - Previous Indian Base Rate) / Previous Indian Base Rate) × 100
  const baseRateChangePercent = previousIndianBaseRate !== 0 
    ? ((currentIndianBaseRate - previousIndianBaseRate) / previousIndianBaseRate) * 100 
    : 0;
  
  const sign = getSign(netChange);
  
  // State Adjustment Amount = Indian Base Rate × State Constraint%
  const stateAdjustmentAmount = currentIndianBaseRate * (Number(adj) / 100);
  
  return (
    <div className="rc-state-rate-compact">
      {/* Primary Rate Display with Indicator */}
      <div className={`rc-rate-primary rc-rate-primary--${sign}`}>
        <span className={`rc-rate-value rc-rate-value--${sign}`}>
          {fmtRupee(currentStateRate)}
        </span>       
      </div>

      {/* Compact Metrics Grid */}
      <div className="rc-metrics-grid">
        {/* Net Change (₹) */}
        <div className={`rc-metric-compact rc-metric--change ${sign}`}>
          <div className={`rc-metric-value-compact rc-metric-value-compact--${sign}`}>
            {sign === "positive" ? "+" : ""}
            {fmtRupee(netChange)}
          </div>
          <div className="rc-tooltip-compact">
            Net Change: {sign === "positive" ? "Increased" : sign === "negative" ? "Decreased" : "No Change"}
          </div>
        </div>
        
        {/* Change % based on Indian Base Rate */}
        <div className={`rc-metric-compact rc-metric--pct ${sign}`}>
          <div className={`rc-metric-value-compact rc-metric-value-compact--${sign}`}>
            {sign === "positive" ? "+" : ""}
            {baseRateChangePercent.toFixed(2)}%
          </div>
          <div className="rc-tooltip-compact">
            Base Rate Change: {Math.abs(baseRateChangePercent).toFixed(2)}%
          </div>
        </div>

        {/* Adjustment Amount */}
        <div className="rc-metric-compact rc-metric--adj">
          <div className="rc-metric-label-compact">Adj ₹</div>
          <div className="rc-metric-value-compact rc-metric-value-compact--zero">
            {fmtRupee(stateAdjustmentAmount)}
          </div>
          <div className="rc-tooltip-compact">
            State Adjustment Amount
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Helper Components
// ─────────────────────────────────────────────

/**
 * Badge for percentage changes with icon
 */
export const PctBadge = ({ value }) => {
  const sign = getSign(value);
  const Icon =
    sign === "positive" ? TrendingUp : sign === "negative" ? TrendingDown : Minus;

  return (
    <span className={`rc-pct-badge rc-pct-badge--${sign}`}>
      <Icon size={10} />
      {fmtPct(value)}
    </span>
  );
};

/**
 * Styled rupee badge
 */
export const RupeeBadge = ({ value, sign = null }) => {
  const computedSign = sign || getSign(value);
  return (
    <span className={`rc-rupee-badge rc-rupee-badge--${computedSign}`}>
      {computedSign === "positive" ? "+" : ""}
      {fmtRupee(value)}
    </span>
  );
};
